import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCart, useShipping, useRegion, useSettings } from '@root/MyChoice/context';
import { LineItem, Summary } from './components';
import Close from '@root/lib/components/Icons/Close';
import { calculateShippingPriceFromRules } from '@root/lib/services/MiddlewareService';
import cn from 'classnames';
import './Sidecart.scss';

const Sidecart = ({
  onClose,
  open,
}) => {
  const [ cart, cartApi ] = useCart();
  const appliedDiscount = cartApi.getDiscountCode();
  const [ settings ] = useSettings();

  const [ shipping ] = useShipping();
  const [ region ] = useRegion();
  const evaluatedShipping = useMemo(() => {
    // If shipping line discount applied, no need to process the rules, shipping is already free
    if (appliedDiscount?.targetType === 'SHIPPING_LINE') {
      return {
        price: 0,
        originalPrice: shipping?.price ?? 0,
      }
    }

    if (shipping.loading || !region || !cart.lineItems) {
      return {
        loading: shipping.loading || !region || !cart.lineItems,
      };
    }

    const customer = window.Shopify.customer || {tags: []};
    const data = calculateShippingPriceFromRules(shipping.rules, cart.lineItems, region, customer, shipping.price);
    const progressibleRules = data.evaluatedRules.filter(r => r.progressible && !r.passed);
    return {
      ...data,
      originalPrice: shipping.price ?? 0,
      progressibleRules,
      closestProgressibleRule: progressibleRules.reduce((closest, rule) => {
        return !closest || rule.progress > closest.progress ? rule : closest;
      }, null), 
    }
  }, [shipping, cart.lineItems, region, appliedDiscount]);
  const innerRef = useRef();

  /**
   * Updates the quantity of an existing line item
   */
  const updateLineItemQuantity = useCallback((lineItemId) => async (qty) => {
    await cartApi.updateItems({
      id: lineItemId,
      quantity: qty,
    });
  }, [cartApi.updateItems]);

  // Making the background not scrollable when open
  useEffect(() => {
    const elements = [document.documentElement, document.body];
    elements.forEach(el => {
      const clazz = 'sidecart-no-scroll';
      open ? el.classList.add(clazz) : el.classList.remove(clazz);
    });
  }, [open]);

  return (
    <div
      style={{
        '--header-and-footer-bg': settings.sidecartHeaderAndFooterColor,
      }}
      className={cn({
        'Sidecart': true,
        'Sidecart--open': open,
      })}
    >
      <div className="Sidecart__Inner" ref={innerRef}>
        <div className="Sidecart__Header">
          <Close className="Sidecart__Close" onClick={onClose} />
          <div className="Sidecart__Title">Your Meals ({cart.itemCount ?? 0})</div>
          {cart.itemCount > 0 && (
            <button className="sidecart-header-remove-all Sidecart__RemoveAll" onClick={cartApi.clear} disabled={cartApi.isLoading('clear')}>
              Remove All
            </button>
          )}
          <hr />
          {evaluatedShipping?.closestProgressibleRule && (
            <div
              className="Sidecart__Motivator"
              style={{'--progress': evaluatedShipping.closestProgressibleRule.progress}}
              dangerouslySetInnerHTML={{__html: 
                evaluatedShipping.closestProgressibleRule.how
                  .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
                  .replace(/item/g, 'Meal')
              }}
            />
          )}
        </div>
        <div className="Sidecart__Items">
          {cart.lineItems?.map?.(lineItem =>
            <LineItem
              key={lineItem.id}
              discounts={lineItem.discounts ?? []}
              unitPrice={lineItem.unitPrice}
              quantity={lineItem.quantity}
              title={lineItem.title}
              variant={lineItem.variant}
              linePrice={lineItem.linePrice}
              onQuantityChange={updateLineItemQuantity(lineItem.id)}
              disabled={cartApi.isLoading('updateItems')}
            />
          )}
          {!cartApi.isLoading('init') && cart.lineItems?.length === 0 && (
            <span className="Sidecart__NoMeals">
              No Meals Added!
            </span>
          )}
        </div>
        <Summary
          evaluatedShipping={evaluatedShipping}
        />
      </div>
    </div>
  );
}

export default Sidecart;
